import { ITEM_STATUSES, ITEM_STATUS_RULES, lowStock } from '../catalog/catalog';

/**
 * Low Stock (piece 26; decision 0508). 🔨 A line crossing the
 * `lowStockThreshold` on that item's `ItemSiteStock` row raises a Checklist
 * item and NEVER a Purchase Order. Nobody wants a system that buys things on
 * its own, and the threshold is per item and per site: a van holding two of a
 * part and a shop holding two are judged against their own numbers (decision
 * 0122).
 *
 * 🔨 Nothing fires for an ActiveMature, a Discontinued or an Inactive Item, at
 * any level of stock (decision 11200). Each is leaving the catalog or has
 * left it, so replenishing it is the opposite of what the status says.
 */

/** The hour key the nightly run registers under, beside the ledger's own (decision 0508). */
export const RUN_LOW_STOCK_JOB = 'runLowStock';
export const RUN_LOW_STOCK_HOUR_KEY = 'inventory.lowStockRunHour';
export const RUN_LOW_STOCK_DEFAULT_HOUR = 6;

/** The `ChecklistItem` kind a crossing raises (decision 0508); it closes when the shelf is back above its threshold. */
export const LOW_STOCK_KIND = 'LowStock';

/** 🔨 The statuses that never raise a low-stock item, whatever the shelf holds: every status that is never replenished (decision 11200). */
export const NO_LOW_STOCK_STATUSES: readonly string[] = ITEM_STATUSES.filter((status) => !ITEM_STATUS_RULES[status].replenished);

export interface LowStockCandidate {
  /**
   * 🔒 The `ItemSiteStock` row the threshold sits on, which is the one key that
   * names an item AND a site together (decision 0508). The raised to-do points
   * at it, so a second item for the same shelf cannot be raised while one is
   * open, and two sites still raise two items.
   */
  itemSiteStockId: string;
  itemId: string;
  itemSku: string;
  itemName: string;
  itemStatus: string;
  siteId: string;
  siteName: string;
  onHand: number;
  threshold: number;
}

export interface LowStockOutcome extends LowStockCandidate {
  /** True where the shelf is at or below the threshold and the item's status allows an item to be raised. */
  raises: boolean;
  /** Why nothing is raised, where nothing is; null where an item is due. */
  reason: string | null;
}

/**
 * The decision for one item and site. 🔒 Whether it raises is the catalog's
 * one rule, `lowStock` (decision 11200), so the report, the run and every
 * other reader agree; this adds only the sentence saying why nothing is due.
 * At the threshold counts as crossing it, and an Item never replenished never
 * raises.
 */
export function lowStockOutcome(candidate: LowStockCandidate): LowStockOutcome {
  if (lowStock(candidate.itemStatus, candidate.onHand, candidate.threshold)) return { ...candidate, raises: true, reason: null };
  if (NO_LOW_STOCK_STATUSES.includes(candidate.itemStatus)) {
    return { ...candidate, raises: false, reason: `the item is ${candidate.itemStatus}, and that status is never replenished` };
  }
  if (!(candidate.threshold > 0)) return { ...candidate, raises: false, reason: 'the site holds no threshold above zero for this item' };
  return { ...candidate, raises: false, reason: 'the shelf is above its threshold' };
}

/**
 * 🔒 One open item per item and site is enough (decision 0508): a second while
 * one is open is noise, and a nightly job that raised one a night would bury
 * every other to-do a person holds.
 */
export function lowStockSourceKey(itemId: string, siteId: string): string {
  return `${itemId}:${siteId}`;
}

/** What the raised `ChecklistItem` records as its source: the shelf's own row, never the Item alone. */
export const LOW_STOCK_SOURCE_TYPE = 'ItemSiteStock';

export function lowStockTitle(candidate: { itemSku: string; itemName: string; siteName: string }): string {
  return `Low stock: ${candidate.itemSku} ${candidate.itemName} at ${candidate.siteName}`;
}

/**
 * 🔒 The threshold an item was raised against, written into its own
 * description, which is what a later run matches a closed item on (decision
 * 0508). The trailing stop is part of the note: without it a threshold of 5
 * matches an item raised against 50.
 */
export function lowStockThresholdNote(threshold: number): string {
  return `against a threshold of ${threshold}.`;
}

export function lowStockDescription(candidate: { onHand: number; threshold: number; siteName: string }): string {
  return `${candidate.siteName} holds ${candidate.onHand} ${lowStockThresholdNote(candidate.threshold)} Nothing has been ordered; raising a purchase order is a person’s decision.`;
}

/**
 * 🔒 Whether the shelf has been above its threshold at any point since a
 * closed item answered for it (decision 0508). The deltas are the stock
 * movements after that moment, newest first, and the walk back from what is on
 * the shelf now reaches what was on it then, checking every step.
 *
 * 🔨 The last step is the shelf as it stood when the item closed, which is
 * what tells a job-closed item from a ticked one: the job closes on a shelf
 * above its threshold, so that closure never suppresses the next shortage.
 */
export function shelfAboveThresholdSince(onHandNow: number, deltasNewestFirst: readonly number[], threshold: number): boolean {
  let running = onHandNow;
  if (running > threshold) return true;
  for (const delta of deltasNewestFirst) {
    running -= delta;
    if (running > threshold) return true;
  }
  return false;
}

/** The reason a run gives for raising nothing where a closed item still answers for the shelf. */
export const LOW_STOCK_ALREADY_ANSWERED = 'a low-stock item for this shelf was closed at this threshold and the shelf has not been above it since';
